import { useCallback, useEffect } from 'react'

import { useSelector, useDispatch } from 'APP/Hooks/redux'
import { isAccessTokenValid } from 'APP/Lib/AuthHelpers'
import { logger } from 'APP/Lib/Datadog'
import { selectAccessToken, selectMattermostUserId } from 'APP/Redux/LoginRedux'
import { selectIsAppActive, selectIsAppOnline } from 'APP/Sagas/AppSessionSagas'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'

/**
 * Watcher component that manages the necessary connections to Mattermost for receiving new chat posts.
 * When the following conditions are met, we want to be connected to Mattermost:
 * - The user is logged in with a valid access token.
 * - The user has a Mattermost user profile.
 * - The app is currently foregrounded/active.
 */
export const MattermostManager = (_props: { testID?: string } = {}) => {
  const dispatch = useDispatch()
  const accessToken = useSelector(selectAccessToken)
  const appIsActive = useSelector(selectIsAppActive)
  const appIsOnline = useSelector(selectIsAppOnline)
  const mattermostUserId = useSelector(selectMattermostUserId)

  const startMattermost = useCallback(() => {
    dispatch(PatientHistoryActions.mattermostStart())
  }, [dispatch])

  const stopMattermost = useCallback(() => {
    dispatch(PatientHistoryActions.mattermostStop())
  }, [dispatch])

  useEffect(() => {
    const accessTokenIsValid = isAccessTokenValid(accessToken)

    logger.info('[MattermostManager] Syncing state...', {
      accessTokenIsValid,
      appIsActive,
      appIsOnline,
      mattermostUserId,
    })

    if (appIsActive && accessTokenIsValid && mattermostUserId) {
      logger.info('[MattermostManager] Will ensure we have a Mattermost connection')
      startMattermost()
    } else {
      logger.info('[MattermostManager] Dependency requirements not met, will not connect', {
        appIsActive,
        accessTokenIsValid,
        mattermostUserId,
      })
    }

    // This cleanup runs everytime a dependency changes
    // i.e. if appIsActive flag changes to inactive, this runs and stops mattermost websocket and long polling.
    return () => {
      logger.info('[MattermostManager] Dependency has changed, disconnecting')
      stopMattermost()
    }
  }, [accessToken, appIsActive, appIsOnline, mattermostUserId, startMattermost, stopMattermost])

  return null
}
